import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import ApiClient from 'Api/ApiClient';
import AuthContext from 'context/AuthContext';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { UrlHelpers } from 'urlHelpers/urlHelper';

import Loader from 'components/Loader/Loader';

const STEPS = {
  CONFIRM: 'confirm',
  RESULT: 'result',
};

const dialogPaperSx = {
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(16, 42, 67, 0.18)',
  p: 1,
  m: { xs: 2, sm: 4 },
  width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
  maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
};

const titleSx = {
  fontSize: { xs: '20px !important', sm: '24px !important' },
  fontWeight: 700,
  color: '#0F172A',
};

const actionsSx = {
  mt: { xs: 3, sm: 4 },
  display: 'flex',
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  justifyContent: 'space-between',
  gap: 1.5,
};

const actionButtonWidth = { xs: '100%', sm: 'auto' };

const labelSx = {
  fontSize: '12px !important',
  fontWeight: 700,
  letterSpacing: '0.6px',
  color: '#5B7184',
  textTransform: 'uppercase',
};

const valueSx = {
  fontSize: { xs: '16px !important', sm: '18px !important' },
  fontWeight: 700,
  color: '#102A43',
  overflowWrap: 'anywhere',
};

const apiClient = new ApiClient();

function ResetPasswordDialog({ open, customer, onClose }) {
  const { initiateUiResponse } = useContext(AuthContext);
  const [step, setStep] = useState(STEPS.CONFIRM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const username = customer?.username || 'this customer';
  const email = customer?.email || '';

  const handleExited = () => {
    setStep(STEPS.CONFIRM);
    setSubmitting(false);
    setSuccess(false);
    setErrorMessage('');
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleResetPassword = async () => {
    setSubmitting(true);
    try {
      await apiClient.put(
        UrlHelpers.autogeneratePassword(customer?.preferredusernm || customer?.username),
        {
          emailNotification: true,
          enforceChangeOnNextLogon: true,
          passwordNeverExpires: true,
          autoReset: true,
          username: customer?.username,
        },
        {
          headers: {
            'x-api-key': initiateUiResponse?.REACT_APP_X_API_KEY_ADMIN,
            appid: 'fis',
            lang: 'EN',
          },
        },
        initiateUiResponse?.REACT_APP_ADMIN_TOKEN
      );
      setSuccess(true);
      setErrorMessage('');
    } catch {
      setSuccess(false);
      setErrorMessage('We are unable to process your request. Please try again later.');
    } finally {
      setSubmitting(false);
      setStep(STEPS.RESULT);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: handleExited }}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Loader open={submitting} />
      {step === STEPS.CONFIRM && (
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography sx={titleSx}>Reset password</Typography>
          <Typography sx={{ mt: 1, fontSize: '15px !important', color: '#475569' }}>
            A temporary password will be emailed to the customer.
          </Typography>

          <Box
            sx={{
              mt: { xs: 2, sm: 3 },
              p: { xs: 2, sm: 2.5 },
              borderRadius: '10px',
              backgroundColor: '#F5F7FB',
              border: '1px solid #E4E8ED',
            }}
          >
            <Typography sx={{ fontSize: '15px !important', color: '#334155', lineHeight: 1.6 }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#0F172A' }}>
                Scope reminder:
              </Box>
              The customer will be prompted to immediately reset their password upon next sign-in.
            </Typography>
          </Box>

          <Stack spacing={2.5} sx={{ mt: 3 }}>
            <Box>
              <Typography sx={labelSx}>USERNAME</Typography>
              <Typography sx={valueSx}>{username}</Typography>
            </Box>
            <Box>
              <Typography sx={labelSx}>Email destination</Typography>
              <Typography sx={valueSx}>{email || 'N/A'}</Typography>
            </Box>
          </Stack>

          <Box sx={actionsSx}>
            <Button
              onClick={handleClose}
              variant="outlined"
              disableRipple
              sx={{
                width: actionButtonWidth,
                minWidth: 100,
                height: 44,
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#D9DEE5',
                color: '#334155',
                fontWeight: 600,
                fontSize: '15px !important',
                '&:hover': { borderColor: '#B7C0CC', backgroundColor: '#F8F9FB' },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleResetPassword}
              disabled={submitting}
              variant="contained"
              disableRipple
              sx={{
                width: actionButtonWidth,
                height: 44,
                px: 3,
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#6B21D8',
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px !important',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#5A18BC', boxShadow: 'none' },
                '&.Mui-disabled': { backgroundColor: '#B79AE8', color: '#fff' },
              }}
            >
              {submitting ? 'Sending...' : 'Send temporary password'}
            </Button>
          </Box>
        </DialogContent>
      )}

      {step === STEPS.RESULT && (
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: success ? '#DEF3E4' : '#FDECEC',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
            }}
          >
            {success ? (
              <CheckIcon sx={{ color: '#1B9E4B', fontSize: 34 }} />
            ) : (
              <CloseIcon sx={{ color: '#D14343', fontSize: 34 }} />
            )}
          </Box>

          {success && <Typography sx={{ ...titleSx, mt: 2 }}>Temporary password sent</Typography>}
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: '15px !important', sm: '17px !important' },
              overflowWrap: 'anywhere',
              color: success ? '#475569' : '#d71714',
            }}
          >
            {success
              ? `Password reset successfully initiated. Temporary credentials sent to ${
                  email || 'the customer'
                }.`
              : errorMessage || 'We are unable to process your request. Please try again later.'}
          </Typography>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={handleClose}
              variant="contained"
              disableRipple
              sx={{
                minWidth: 120,
                height: 44,
                borderRadius: '8px',
                textTransform: 'none',
                backgroundColor: '#6B21D8',
                color: '#fff',
                fontWeight: 700,
                fontSize: '15px !important',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#5A18BC', boxShadow: 'none' },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}

ResetPasswordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  customer: PropTypes.shape({
    username: PropTypes.string,
    email: PropTypes.string,
    registeredfrom: PropTypes.string,
    preferredusernm: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

ResetPasswordDialog.defaultProps = {
  customer: null,
};

export default ResetPasswordDialog;
